// Exports: useScrollAnchor
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useLayoutEffect, useRef } from "react";

const findFallbackAnchor = (scroller) => {
  if (!scroller) return null;

  const scrollerRect = scroller.getBoundingClientRect();
  const anchors = scroller.querySelectorAll("[data-scroll-anchor]");

  for (const anchor of anchors) {
    const rect = anchor.getBoundingClientRect();
    if (rect.bottom >= scrollerRect.top) {
      return anchor;
    }
  }

  return null;
};

const useScrollAnchor = (dependencies = []) => {
  const scrollContainerRef = useRef(null);
  const scrollAnchorRef = useRef(null);
  const restorePendingRef = useRef(false);

  const storeAnchor = useCallback((anchorEl) => {
    const scroller = scrollContainerRef.current;
    if (!scroller) return;

    const targetAnchor = anchorEl || findFallbackAnchor(scroller);
    if (!targetAnchor) {
      scrollAnchorRef.current = {
        key: null,
        anchorTop: 0,
        scrollTop: scroller.scrollTop,
      };
      restorePendingRef.current = true;
      return;
    }

    const key = targetAnchor.getAttribute("data-scroll-anchor");
    if (!key) return;

    const scrollerTop = scroller.getBoundingClientRect().top;
    scrollAnchorRef.current = {
      key,
      anchorTop: targetAnchor.getBoundingClientRect().top - scrollerTop,
      scrollTop: scroller.scrollTop,
    };
    restorePendingRef.current = true;
  }, []);

  const captureScrollAnchor = useCallback(
    (event) => {
      const scroller = scrollContainerRef.current;
      const target = event.target;

      if (!scroller || !(target instanceof Element)) return;

      const anchorEl = target.closest("[data-scroll-anchor]");
      if (anchorEl && scroller.contains(anchorEl)) {
        storeAnchor(anchorEl);
      }
    },
    [storeAnchor]
  );

  const preserveScroll = useCallback(
    (anchorSelectorOrFn, maybeFn) => {
      const anchorSelector =
        typeof anchorSelectorOrFn === "string" ? anchorSelectorOrFn : null;
      const callback =
        typeof anchorSelectorOrFn === "function"
          ? anchorSelectorOrFn
          : maybeFn;

      const scroller = scrollContainerRef.current;
      if (!scroller) {
        return typeof callback === "function" ? callback() : undefined;
      }

      if (anchorSelector) {
        storeAnchor(scroller.querySelector(anchorSelector));
      } else {
        storeAnchor(null);
      }

      return typeof callback === "function" ? callback() : undefined;
    },
    [storeAnchor]
  );

  useLayoutEffect(() => {
    const scroller = scrollContainerRef.current;
    const anchor = scrollAnchorRef.current;

    if (!restorePendingRef.current || !scroller || !anchor) return;

    restorePendingRef.current = false;

    if (anchor.key) {
      const anchorEl = scroller.querySelector(
        `[data-scroll-anchor="${anchor.key}"]`
      );

      if (anchorEl) {
        const scrollerTop = scroller.getBoundingClientRect().top;
        const anchorTopNow =
          anchorEl.getBoundingClientRect().top - scrollerTop;
        scroller.scrollTop += anchorTopNow - anchor.anchorTop;
        return;
      }
    }

    if (typeof anchor.scrollTop === "number") {
      scroller.scrollTop = anchor.scrollTop;
    }
  }, [...dependencies]);

  return {
    scrollContainerRef,
    captureScrollAnchor,
    preserveScroll,
  };
};

export default useScrollAnchor;
